import type * as React from 'react';

import {
  baseMain80,
  grayLight,
  greenLight,
  redLight,
  yellowLight,
} from '@crehana/ui/dist/styles/colors';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  ADVANCE,
  BEGINNER,
  INTERMEDIATE,
} from '@/shared/crehana-jsx/SkillIQ/strings';

import { SCORE_RANGE } from '../../constants';
import { Levels, LevelsMobile, Root } from './Chart.styles';
import { levels } from './constants';
import type { ChartProps, ResultType } from './types';

const setXCoordinate = (val: number) => {
  // f(x)=0.0004(x - 46)^3 + 39
  return Math.cbrt((val - 39) / 0.0004) + 46;
};

const levelColor = {
  low: redLight,
  medium: yellowLight,
  high: greenLight,
};

const Chart: React.FC<React.PropsWithChildren<ChartProps>> = ({
  metricsResult,
}) => {
  const results = metricsResult.reduce(
    (acc, current) => {
      if (current.cy <= 50) {
        return {
          ...acc,
          low: [
            ...acc.low,
            {
              ...current,
              cx: setXCoordinate(current.cy),
              label: current.label,
            },
          ],
        };
      }
      if (current.cy <= 80 && current.cy >= 51) {
        return {
          ...acc,
          medium: [
            ...acc.medium,
            {
              ...current,
              cx: setXCoordinate(current.cy),
              label: current.label,
            },
          ],
        };
      }
      return {
        ...acc,
        high: [
          ...acc.high,
          { ...current, cx: setXCoordinate(current.cy), label: current.label },
        ],
      };
    },
    { low: [], medium: [], high: [] } as ResultType,
  );

  const renderLegend = () => (
    <Levels className="hidden md:z-2 md:absolute md:block flex flex-col">
      <div className="pr-12 advance-level flex flex-col items-center justify-center">
        <p className="font-caption text-green-main">{ADVANCE}</p>
        <p className="font-label text-white block mt-4">
          {SCORE_RANGE.ADVANCE_RANGE}
        </p>
      </div>
      <div className="pr-12 intermediate-level flex flex-col items-center justify-center">
        <p className="font-caption text-yellow-light">{INTERMEDIATE}</p>
        <p className="font-label text-white block mt-4">
          {SCORE_RANGE.INTERMEDIATE_RANGE}
        </p>
      </div>
      <div className="pr-12 beginner-level flex flex-col items-center justify-center">
        <p className="font-caption text-red-light">{BEGINNER}</p>
        <p className="font-label text-white block mt-4">
          {SCORE_RANGE.BEGINNER_RANGE}
        </p>
      </div>
    </Levels>
  );
  const renderLegendMobile = () => (
    <LevelsMobile className="md:hidden flex items-center justify-between">
      <div className="flex flex-col justify-center items-center">
        <p className="font-caption text-red-light">{BEGINNER}</p>
        <p className="font-label text-gray-dark block mt-4">
          {SCORE_RANGE.BEGINNER_RANGE}
        </p>
      </div>
      <div className="flex flex-col justify-center items-center">
        <p className="font-caption text-yellow-light">{INTERMEDIATE}</p>
        <p className="font-label text-gray-dark block mt-4">
          {SCORE_RANGE.INTERMEDIATE_RANGE}
        </p>
      </div>
      <div className="flex flex-col justify-center items-center">
        <p className="font-caption text-green-main">{ADVANCE}</p>
        <p className="font-label text-gray-dark block mt-4">
          {SCORE_RANGE.ADVANCE_RANGE}
        </p>
      </div>
    </LevelsMobile>
  );

  const CustomTooltip = ({ ...props }) => {
    const { active, payload } = props;

    if (active) {
      return (
        <div className="p-8 flex flex-col items-center">
          <p className="font-h4 text-white">{payload[1].value}</p>
          <p
            className="font-label text-white text-center"
            style={{ width: 64 }}
          >
            {payload[1].payload.label}
          </p>
        </div>
      );
    }

    return null;
  };

  const renderLabel = ({
    color,
    ...props
  }: {
    color: string;
    cx?: number;
    cy?: number;
  }) => {
    const { cx, cy } = props;

    return (
      <circle
        className="cursor-pointer"
        cx={cx ? cx + 5 : undefined}
        cy={cy ? cy + 5 : undefined}
        r={8}
        stroke={color}
        strokeWidth={1}
        fill={baseMain80}
      />
    );
  };

  return (
    <Root>
      {renderLegend()}
      <ResponsiveContainer width="100%" height={290}>
        <LineChart>
          <CartesianGrid
            strokeDasharray="2 5"
            stroke={grayLight}
            strokeWidth="1px"
            vertical={false}
            horizontalPoints={[5, 45, 102]}
          />
          <XAxis
            tickMargin={0}
            tickLine={false}
            stroke={grayLight}
            tick={false}
            type="number"
            domain={[0, 100]}
            dataKey="category"
            allowDuplicatedCategory={false}
          />
          <YAxis
            tickMargin={0}
            tickLine={false}
            tick={false}
            dataKey="value"
            stroke={grayLight}
            domain={[0, 100]}
          />
          {levels.map(l => (
            <Line
              dot={false}
              type="basis"
              dataKey="value"
              data={l.data}
              name={l.name}
              key={l.name}
              strokeWidth="2px"
              stroke={l.stroke}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <ResponsiveContainer
        width="100%"
        height={290}
        className="scatter-container"
      >
        <ScatterChart>
          <CartesianGrid horizontal={false} vertical={false} />
          <XAxis
            tickMargin={0}
            tickLine={false}
            axisLine={false}
            tick={false}
            type="number"
            domain={[0, 100]}
            dataKey="cx"
          />
          <YAxis
            tickMargin={0}
            tickLine={false}
            axisLine={false}
            tick={false}
            type="number"
            stroke={grayLight}
            domain={[0, 100]}
            dataKey="cy"
          />
          <Tooltip content={<CustomTooltip payload={results} />} />
          {Object.keys(results).map((el, idx) => (
            <Scatter
              key={results[el as keyof typeof results][idx]?.label}
              name={el as keyof typeof results}
              data={results[el as keyof typeof results]}
              shape={renderLabel({
                color: levelColor[el as keyof typeof results],
              })}
            />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
      {renderLegendMobile()}
    </Root>
  );
};

export default Chart;
